import { cx } from '@twind/core';
import { useEffect, useState, useCallback } from 'react';
import useVirtualAssistantAvatar from 'app/modules/virtual-assistant/hooks/useVirtualAssistantAvatar';
import { ChatHandlerParams } from 'app/modules/virtual-assistant/types/chat';
import { createUserMessage } from 'app/modules/virtual-assistant/utils/chatItemUtils';

import MessageBase from '../BaseChatItem';
import {
  AnswerItem,
  ChatBotFormAnswer,
  ChatFormItem,
  ChatFormItemType,
  ChatItemForm,
  ChatItemFormType,
  ChatItemSourceType,
} from '../../../../../services/chatBot';
import FormChatItemInput from './FormChatItemInput';
import FormChatItemSingleChoice from './FormChatItemSingleChoice';
import ContactFormChatItem from '../ContactFormChatItem';
import MessageChatItem from '../MessageChatItem';

export default function FormChatItem({
  chatItem,
  answerItem,
  className,
  onAnswer,
  onDismiss,
  onDisplayFormItem,
  shouldAnimate,
}: {
  chatItem: ChatItemForm;
  answerItem?: AnswerItem;
  className?: string;
  onAnswer: (params: ChatHandlerParams) => Promise<void>;
  onDismiss: (params: ChatHandlerParams) => Promise<void>;
  onDisplayFormItem?: (formItem: ChatFormItem, chatItem: ChatItemForm) => void;
  shouldAnimate?: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [input, setInput] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const avatarURL = useVirtualAssistantAvatar();

  useEffect(() => {
    if (!onDisplayFormItem) return;
    chatItem.items.forEach((item) => {
      onDisplayFormItem(item, chatItem);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasErrors = chatItem.items.some((item) => {
    const answerFormItem = answerItem?.items?.find((answerFormItem) => answerFormItem.ref_id === item.ref_id);
    return answerFormItem?.errors && answerFormItem.errors.length > 0;
  });

  const createFormAnswer = useCallback(
    (itemRefId: string, value: string): ChatBotFormAnswer => {
      return {
        form_ref_id: chatItem.ref_id,
        item_ref_id: itemRefId,
        value,
      };
    },
    [chatItem.ref_id]
  );

  const handleInputSubmit = useCallback(
    (item: ChatFormItem) => {
      onAnswer({
        answer: createFormAnswer(item.ref_id, input),
        source: ChatItemSourceType.PATIENT,
        formItem: item,
        chatItem,
        options: { createMessage: true },
      });
      setSubmitted(true);
    },
    [onAnswer, input, chatItem, createFormAnswer]
  );

  const handleInputSkip = useCallback(
    (
      item: ChatFormItem,
      { createMessage = true, showTypingArea = false }: { createMessage?: boolean; showTypingArea?: boolean } = {}
    ) => {
      onDismiss({
        answer: createFormAnswer(item.ref_id, 'Skipped'),
        formItem: item,
        chatItem,
        options: { createMessage, showTypingArea },
      });
      setSubmitted(true);
    },
    [onDismiss, chatItem, createFormAnswer]
  );

  const handleChoiceSelect = useCallback(
    (
      item: ChatFormItem,
      choice: string,
      source: ChatItemSourceType,
      { createMessage = true, showTypingArea = false }: { createMessage?: boolean; showTypingArea?: boolean } = {}
    ) => {
      setSelected(choice);
      onAnswer({
        answer: createFormAnswer(item.ref_id, choice),
        source,
        formItem: item,
        chatItem,
        options: { createMessage, showTypingArea },
        onError: () => {
          setSelected('');
        },
      });
    },
    [onAnswer, chatItem, createFormAnswer]
  );

  const findAnswerFormItem = useCallback(
    (item: ChatFormItem) => {
      return answerItem?.items?.find((answerItem) => answerItem.ref_id === item.ref_id);
    },
    [answerItem]
  );

  const shouldHideIfSubmitted = chatItem.form_type !== ChatItemFormType.CONTACT_FORM;

  if ((submitted || selected) && !hasErrors && shouldHideIfSubmitted) return null;

  if (chatItem.form_type === ChatItemFormType.CONTACT_FORM) {
    return (
      <ContactFormChatItem
        onClickCancel={() => {
          handleInputSkip(chatItem.items[0], { createMessage: false, showTypingArea: true });
        }}
        onComplete={() => {
          handleChoiceSelect(chatItem.items[0], 'continue', ChatItemSourceType.PATIENT, {
            createMessage: false,
            showTypingArea: true,
          });
        }}
        chatItem={chatItem}
      />
    );
  }

  const renderFormItem = (item: ChatFormItem) => {
    switch (item.item_type) {
      case ChatFormItemType.INPUT: {
        const answerFormItem = findAnswerFormItem(item);
        const hasError = answerFormItem?.errors && answerFormItem.errors.length > 0;

        return (
          <FormChatItemInput
            key={item.ref_id}
            submitted={submitted}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleInputSubmit(item);
              }
            }}
            onChange={(e) => setInput(e.target.value)}
            onClickSubmit={() => handleInputSubmit(item)}
            onClickSkip={() => handleInputSkip(item)}
            formItem={item}
            answerFormItem={answerFormItem}
            hasError={!!hasError}
            value={input}
          />
        );
      }
      case ChatFormItemType.SINGE_CHOICE:
        if (chatItem.completed) {
          return <MessageChatItem chatItem={createUserMessage(item.value || '')} />;
        }
        return (
          <FormChatItemSingleChoice
            key={item.ref_id}
            selected={selected}
            formItem={item}
            onClickAnswerChoice={(choice) => handleChoiceSelect(item, choice, ChatItemSourceType.PATIENT_CHOICE)}
            shouldAnimate={shouldAnimate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <MessageBase
      avatarURL={avatarURL}
      showAvatar={false}
      className={cx('ostro-chatbot-message-form', className)}
    >
      {chatItem.items.map(renderFormItem)}
    </MessageBase>
  );
}
